import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import WhatsAppButton from "./components/WhatsAppButton";
import api from "./lib/api";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboard from "./pages/AdminDashboard";
import TrackingPage from "./pages/TrackingPage";
import RatesPage from "./pages/RatesPage";
import SettingsPage from "./pages/SettingsPage";
import DestinationPage from "./pages/DestinationPage";
import ProPage from "./pages/ProPage";
import FretPage from "./pages/FretPage";
import FretDestinationPage from "./pages/FretDestinationPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import DealsPage from "./pages/DealsPage";
import DealDetailPage from "./pages/DealDetailPage";
import DealProductPage from "./pages/DealProductPage";

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Only Route (redirect if logged in)
function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.is_admin ? "/admin" : "/dashboard"} replace />;
  }

  return children;
}

function PageTracker() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;  // don't count admin browsing
    let sid = localStorage.getItem('ism_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('ism_sid', sid);
    }
    api.post('/track', {
      path: location.pathname,
      referrer: document.referrer || '',
      session_id: sid,
    }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <PageTracker />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/tracking" element={<TrackingPage />} />
      <Route path="/rates" element={<RatesPage />} />
      <Route path="/pro" element={<ProPage />} />
      <Route path="/fret" element={<FretPage />} />
      <Route path="/fret/:slug" element={<FretDestinationPage />} />
      <Route path="/abonnements" element={<SubscriptionPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/destination/:slug" element={<DestinationPage />} />
      <Route path="/deals" element={<DealsPage />} />
      <Route path="/deals/:slug" element={<DealDetailPage />} />
      <Route path="/deals/:slug/p/:productSlug" element={<DealProductPage />} />
      
      {/* Auth Routes (redirect if logged in) */}
      <Route 
        path="/login" 
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Manrope, sans-serif',
            },
          }}
        />
        <WhatsAppButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
